"use client";

import {useState} from "react";
import {useRouter} from "next/navigation";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {clearedFilters, listingsHref, NO_DEFAULTS, parseFilters, type Filters, type SearchDefaults} from "@/lib/listing-filters";
import type {ListingAreas} from "@/lib/listings";

const selectClass =
    "h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

const COUNTS = [0, 1, 2, 3, 4, 5];

/**
 * The search form. It is a plain GET form, so it works without scripts; with them the
 * address is built without empty values. Applying a filter goes back to page 1.
 */
export default function SearchFilters({filters, defaults, areas}: {
    filters: Filters;
    defaults: SearchDefaults;
    areas: ListingAreas | null;
}) {
    const router = useRouter();
    const [mls, setMls] = useState(filters.mls ?? "");
    // While an MLS® number is typed the other filters are cleared and disabled (they are ignored).
    const locked = mls.trim() !== "";
    const subdivisions = areas?.areas.find((a) => a.slug === filters.area)?.subdivisions ?? [];

    function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const raw: Record<string, string> = {};
        data.forEach((v, k) => {
            if (typeof v === "string") raw[k] = v;
        });
        // An area change drops the old subdivision, which belongs to the old area.
        if (raw.area !== (filters.area ?? "")) delete raw.subdivision;
        // Read without the starting choices: what the form says is what is asked for (an empty
        // area or price is "no filter"), and the address records the difference from them.
        router.push(listingsHref(parseFilters(raw, NO_DEFAULTS), {}, defaults));
    }

    return (
        <form method="get" action="/listings" onSubmit={onSubmit} aria-label="Search listings"
              className="grid gap-4 rounded-lg border bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1 sm:col-span-2 lg:col-span-4">
                <Label htmlFor="f-mls">MLS® number</Label>
                <Input id="f-mls" name="mls" type="text" maxLength={32} autoComplete="off" autoCapitalize="characters"
                       spellCheck={false} placeholder="For example R3051234" value={mls}
                       onChange={(e) => setMls(e.target.value)} aria-describedby="f-mls-hint"/>
                <p id="f-mls-hint" className="text-xs text-muted-foreground">
                    Type a number to go straight to one home; the other filters are cleared.
                </p>
            </div>
            <fieldset key={locked ? "locked" : "open"} disabled={locked} className="contents">
            <legend className="sr-only">Other filters</legend>
            <div className="space-y-1">
                <Label htmlFor="f-area">Area</Label>
                <select id="f-area" name="area" defaultValue={locked ? "" : filters.area ?? ""} className={selectClass}>
                    <option value="">All areas</option>
                    {areas?.areas.map((a) => (
                        <option key={a.slug} value={a.slug}>{a.name} ({a.count})</option>
                    ))}
                </select>
            </div>
            {subdivisions.length > 0 ? (
                <div className="space-y-1">
                    <Label htmlFor="f-subdivision">Neighbourhood</Label>
                    <select id="f-subdivision" name="subdivision" defaultValue={filters.subdivision ?? ""} className={selectClass}>
                        <option value="">All neighbourhoods</option>
                        {subdivisions.map((s) => <option key={s.slug} value={s.slug}>{s.name} ({s.count})</option>)}
                    </select>
                </div>
            ) : null}
            <div className="space-y-1">
                <Label htmlFor="f-type">Property type</Label>
                <select id="f-type" name="type" defaultValue={locked ? "" : filters.type ?? ""} className={selectClass}>
                    <option value="">All types</option>
                    {areas?.types.map((t) => <option key={t.slug} value={t.slug}>{t.name} ({t.count})</option>)}
                </select>
            </div>
            <div className="space-y-1">
                <Label htmlFor="f-min">Minimum price ($)</Label>
                <Input id="f-min" name="minPrice" type="number" inputMode="numeric" min={0} step={10000}
                       defaultValue={locked ? "" : filters.minPrice ?? ""}/>
            </div>
            <div className="space-y-1">
                <Label htmlFor="f-max">Maximum price ($)</Label>
                <Input id="f-max" name="maxPrice" type="number" inputMode="numeric" min={0} step={10000}
                       defaultValue={locked ? "" : filters.maxPrice ?? ""}/>
            </div>
            <div className="space-y-1">
                <Label htmlFor="f-beds">Bedrooms</Label>
                <select id="f-beds" name="beds" defaultValue={locked ? "0" : String(filters.beds ?? 0)} className={selectClass}>
                    {COUNTS.map((n) => <option key={n} value={n}>{n === 0 ? "Any" : `${n}+`}</option>)}
                </select>
            </div>
            <div className="space-y-1">
                <Label htmlFor="f-baths">Bathrooms</Label>
                <select id="f-baths" name="baths" defaultValue={locked ? "0" : String(filters.baths ?? 0)} className={selectClass}>
                    {COUNTS.map((n) => <option key={n} value={n}>{n === 0 ? "Any" : `${n}+`}</option>)}
                </select>
            </div>
            <div className="space-y-1">
                <Label htmlFor="f-sort">Sort by</Label>
                <select id="f-sort" name="sort" defaultValue={filters.sort} className={selectClass}>
                    <option value="newest">Newest first</option>
                    <option value="price_asc">Price, low to high</option>
                    <option value="price_desc">Price, high to low</option>
                </select>
            </div>
            </fieldset>
            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
                <Button type="submit" variant="brand">Search</Button>
                <Button type="button" variant="ghost" onClick={() => { setMls(""); router.push(listingsHref(clearedFilters(filters), {}, defaults)); }}>Clear filters</Button>
            </div>
        </form>
    );
}
